/**
 * useFolderTree
 *
 * - Fetches GET /api/test-case-folders?projectId=
 * - Builds a nested IFolderNode tree from the flat API response
 * - Exposes mutation helpers (create, rename, delete, move) with
 *   optimistic cache invalidation via React Query
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useMemo } from 'react';
import { API_BASE_URL } from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';
import type { ITestFolder, IFolderNode } from '../types';

const API_URL = API_BASE_URL;

/** Build a nested tree from a flat folder list. */
export function buildFolderTree(folders: ITestFolder[]): IFolderNode[] {
    const nodeMap = new Map<string, IFolderNode>();
    const roots: IFolderNode[] = [];

    // First pass: create all nodes
    for (const f of folders) {
        nodeMap.set(f._id, { ...f, children: [], depth: 0 });
    }

    // Second pass: link parents and children
    for (const f of folders) {
        const node = nodeMap.get(f._id)!;
        if (f.parentId && nodeMap.has(f.parentId)) {
            const parent = nodeMap.get(f.parentId)!;
            node.depth = parent.depth + 1;
            parent.children.push(node);
        } else {
            roots.push(node);
        }
    }

    // Sort children by order then name at each level
    function sortChildren(nodes: IFolderNode[]): void {
        nodes.sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
        for (const n of nodes) sortChildren(n.children);
    }
    sortChildren(roots);

    return roots;
}

export function useFolderTree(projectId: string | null) {
    const { token } = useAuth();
    const queryClient = useQueryClient();

    const queryKey = ['test-case-folders', projectId, token];

    const { data, isLoading, isError } = useQuery<{ folders: ITestFolder[] }>({
        queryKey,
        queryFn: async () => {
            if (!projectId) return { folders: [] };
            const { data } = await axios.get(
                `${API_URL}/api/test-case-folders?projectId=${projectId}`,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            return data.data;
        },
        enabled: !!token && !!projectId,
        staleTime: 30_000,
    });

    const folders: ITestFolder[] = data?.folders ?? [];
    const tree: IFolderNode[]    = useMemo(() => buildFolderTree(folders), [folders]);

    function invalidate() {
        queryClient.invalidateQueries({ queryKey: ['test-case-folders', projectId] });
        queryClient.invalidateQueries({ queryKey: ['test-cases'] });
    }

    // ── Mutations ─────────────────────────────────────────────────────────────

    async function createFolder(parentId: string | undefined, name: string) {
        await axios.post(
            `${API_URL}/api/test-case-folders`,
            { projectId, name, parentId },
            { headers: { Authorization: `Bearer ${token}` } },
        );
        invalidate();
    }

    async function renameFolder(id: string, name: string) {
        await axios.put(
            `${API_URL}/api/test-case-folders/${id}`,
            { name },
            { headers: { Authorization: `Bearer ${token}` } },
        );
        invalidate();
    }

    async function deleteFolder(id: string, strategy: 'move' | 'delete', targetFolderId?: string) {
        await axios.delete(
            `${API_URL}/api/test-case-folders/${id}`,
            {
                data: { strategy, targetFolderId },
                headers: { Authorization: `Bearer ${token}` },
            },
        );
        invalidate();
    }

    async function moveFolder(id: string, targetParentFolderId: string | null) {
        await axios.patch(
            `${API_URL}/api/test-case-folders/${id}/move`,
            { targetParentFolderId },
            { headers: { Authorization: `Bearer ${token}` } },
        );
        invalidate();
    }

    return {
        folders,
        tree,
        isLoading,
        isError,
        createFolder,
        renameFolder,
        deleteFolder,
        moveFolder,
    };
}
